import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import openai from '../../../services/openaiClient';
import { TrendingUp, Sparkles, Target, Award, BarChart3, FileBarChart } from 'lucide-react';



const PerformanceActionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('analysis');
  const [loading, setLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [kpiName, setKpiName] = useState('');

  const analyzePerformance = async (analysisType) => {
    setLoading(true);
    try {
      const prompts = {
        overall: 'قم بتحليل شامل للأداء العام للشركة وحدد نقاط القوة والضعف مع توصيات للتحسين',
        team: 'قم بتحليل أداء الفريق وإنتاجية الموظفين وقدم استراتيجيات لتحسين الأداء',
        goals: 'قم بتحليل التقدم نحو تحقيق الأهداف المحددة وقدم توصيات لتسريع الإنجاز',
        benchmarking: 'قم بمقارنة الأداء الحالي مع معايير الصناعة وحدد المجالات التي تحتاج إلى تحسين',
        kpi: `قم بتحليل مؤشر الأداء: ${kpiName} وقدم رؤى حول كيفية تحسينه`
      };

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'أنت محلل أداء متخصص في تقييم أداء الشركات وتقديم استراتيجيات للتحسين المستمر.'
          },
          {
            role: 'user',
            content: prompts?.[analysisType]
          }
        ],
        max_tokens: 500,
        temperature: 0.7
      });

      setAiAnalysis(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error analyzing performance:', error);
      setAiAnalysis('حدث خطأ في التحليل. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'analysis', label: 'تحليل الأداء', icon: Sparkles },
    { id: 'kpis', label: 'مؤشرات الأداء', icon: Target },
    { id: 'reports', label: 'تقارير الأداء', icon: FileBarChart }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="تقارير الأداء - تحليلات متقدمة" size="large" icon={null} iconColor="" footer={null}>
      <div className="flex border-b border-border mb-6">
        {tabs?.map((tab) => {
          const TabIcon = tab?.icon;
          return (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <TabIcon className="w-4 h-4" />
              {tab?.label}
            </button>
          );
        })}
      </div>
      {activeTab === 'analysis' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={() => analyzePerformance('overall')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <BarChart3 className="w-6 h-6" />
              <span>تحليل الأداء العام</span>
            </Button>
            <Button
              onClick={() => analyzePerformance('team')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <Award className="w-6 h-6" />
              <span>تحليل أداء الفريق</span>
            </Button>
            <Button
              onClick={() => analyzePerformance('goals')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <Target className="w-6 h-6" />
              <span>تحليل تحقيق الأهداف</span>
            </Button>
            <Button
              onClick={() => analyzePerformance('benchmarking')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <TrendingUp className="w-6 h-6" />
              <span>مقارنة مع المعايير</span>
            </Button>
          </div>

          <div className="flex gap-2">
            <Input
              placeholder="مؤشر الأداء (مثال: معدل التحويل، رضا العملاء)"
              value={kpiName}
              onChange={(e) => setKpiName(e?.target?.value)}
              className="flex-1"
            />
            <Button
              onClick={() => analyzePerformance('kpi')}
              disabled={loading || !kpiName}
            >
              <Sparkles className="w-4 h-4 ml-2" />
              تحليل
            </Button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="mr-3 text-muted-foreground">جاري تحليل الأداء...</span>
            </div>
          )}

          {aiAnalysis && !loading && (
            <div className="bg-accent/5 border border-accent/20 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-accent" />
                <h3 className="font-semibold text-foreground">تحليل الأداء الذكي</h3>
              </div>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{aiAnalysis}</p>
            </div>
          )}
        </div>
      )}
      {activeTab === 'kpis' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { name: 'معدل التحويل', value: '٦٨٪', target: '٧٥٪', status: 'warning' },
              { name: 'رضا العملاء', value: '٤.٦/٥', target: '٤.٥/٥', status: 'success' },
              { name: 'وقت الاستجابة', value: '٢.٣ ساعة', target: '٢ ساعة', status: 'warning' },
              { name: 'معدل الإغلاق', value: '٣٢٪', target: '٣٠٪', status: 'success' },
              { name: 'إنتاجية الموظف', value: '٨٥٪', target: '٨٠٪', status: 'success' },
              { name: 'معدل الاحتفاظ', value: '٧٢٪', target: '٧٥٪', status: 'danger' }
            ]?.map((kpi, idx) => (
              <div key={idx} className="border border-border rounded-lg p-4">
                <div className="text-xs text-muted-foreground mb-2">{kpi?.name}</div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-2xl font-bold text-foreground">{kpi?.value}</span>
                  <span className="text-xs text-muted-foreground">/ {kpi?.target}</span>
                </div>
                <div className={`text-xs px-2 py-1 rounded inline-block ${
                  kpi?.status === 'success' ? 'bg-success/10 text-success' :
                  kpi?.status === 'warning'? 'bg-warning/10 text-warning' : 'bg-destructive/10 text-destructive'
                }`}>
                  {kpi?.status === 'success' ? 'تحقق الهدف' : kpi?.status === 'warning' ? 'قريب من الهدف' : 'أقل من الهدف'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'تقرير الأداء الشهري', desc: 'ملخص شامل للأداء الشهري' },
              { title: 'تقرير أداء الفريق', desc: 'تحليل أداء الموظفين والفرق' },
              { title: 'تقرير تحقيق الأهداف', desc: 'التقدم نحو تحقيق الأهداف المحددة' },
              { title: 'تقرير المقارنة', desc: 'مقارنة الأداء مع الفترات السابقة' }
            ]?.map((report, idx) => (
              <div key={idx} className="border border-border rounded-lg p-4 hover:border-accent transition-colors cursor-pointer">
                <h4 className="font-medium text-foreground mb-1">{report?.title}</h4>
                <p className="text-xs text-muted-foreground mb-3">{report?.desc}</p>
                <Button size="sm" variant="outline">إنشاء التقرير</Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PerformanceActionsModal;